/**
 * useCountUp — animate a number from its previous value up to `target`.
 *
 * Returns the current display value (a number); feed it to your formatter each
 * frame. Uses requestAnimationFrame with easeOutCubic (fast, then settling).
 * Respects prefers-reduced-motion by jumping straight to the target.
 *
 * On subsequent target changes it animates from the last shown value, so a
 * scenario toggle re-rolls the figure from old → new rather than 0 → new.
 */
import { useEffect, useRef, useState } from "react";

const reducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function useCountUp(target, duration = 900) {
  const end = Number(target);
  const [value, setValue] = useState(Number.isFinite(end) ? 0 : end);
  const fromRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!Number.isFinite(end)) {
      setValue(target);
      return;
    }
    if (reducedMotion()) {
      setValue(end);
      fromRef.current = end;
      return;
    }
    const start = fromRef.current;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setValue(start + (end - start) * eased);
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = end;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [end, target, duration]);

  return value;
}
